import { useState, useEffect } from "react";
import { ChevronRight, ChevronDown, User, Users, Plus, Send, Loader2, Search, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Pagination from "@/components/Pagination";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ResellerNetwork() {
  const { user } = useAuth();
  const [roots, setRoots] = useState([]);
  const [childrenMap, setChildrenMap] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", as_reseller: true, initial_credits: 0 });
  const [q, setQ] = useState("");
  const [sr, setSr] = useState(null); // search results { items, total, page, limit }
  const [refCopied, setRefCopied] = useState(false);
  const refLink = `${window.location.origin}/cadastro?r=${user?.id || ""}`;
  const copyRef = async () => { try { await navigator.clipboard.writeText(refLink); setRefCopied(true); toast.success("Link copiado!"); setTimeout(() => setRefCopied(false), 2000); } catch { toast.error("Não foi possível copiar."); } };

  const loadRoots = () => api.get("/reseller/network").then(({ data }) => setRoots(data)).catch(() => {});
  useEffect(() => { loadRoots(); }, []);

  const searchPage = (p) => api.get(`/reseller/network?q=${encodeURIComponent(q.trim())}&page=${p}&limit=30`).then(({ data }) => setSr(data)).catch(() => {});
  useEffect(() => {
    const t = q.trim();
    if (!t) { setSr(null); return; }
    const id = setTimeout(() => searchPage(1), 300);
    return () => clearTimeout(id);
  }, [q]);

  const toggle = async (node) => {
    const id = node.id;
    if (expanded[id]) {
      setExpanded((e) => ({ ...e, [id]: false }));
      return;
    }
    if (childrenMap[id] === undefined) {
      setLoadingId(id);
      try {
        const { data } = await api.get(`/reseller/network?parent_id=${id}`);
        setChildrenMap((m) => ({ ...m, [id]: data }));
      } catch (e) { toast.error(apiError(e)); setLoadingId(null); return; }
      setLoadingId(null);
    }
    setExpanded((e) => ({ ...e, [id]: true }));
  };

  const renderRow = (node, level) => {
    const kids = childrenMap[node.id];
    const rows = [];
    rows.push(
      <div key={node.id} style={{ marginLeft: level * 18 }} data-testid={`tree-node-${node.id}`}>
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
          {node.children_count > 0 ? (
            <button onClick={() => toggle(node)} className="text-muted-foreground hover:text-foreground" data-testid={`tree-toggle-${node.id}`}>
              {loadingId === node.id ? <Loader2 className="h-4 w-4 animate-spin" /> : expanded[node.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : <span className="inline-block w-4" />}
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
            {node.role === "reseller" ? <Users className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{node.name}</p>
            <p className="truncate text-xs text-muted-foreground">{node.email}</p>
          </div>
          <Badge variant="secondary" className="capitalize text-[10px]">{node.role}</Badge>
          <span className="text-xs text-muted-foreground">{node.credits} cr.</span>
        </div>
      </div>
    );
    if (expanded[node.id] && kids) {
      if (kids.length === 0) {
        rows.push(<p key={node.id + "-empty"} style={{ marginLeft: (level + 1) * 18 }} className="px-3 py-1 text-xs text-muted-foreground">Sem sub-revendedores.</p>);
      } else {
        kids.forEach((c) => rows.push(...renderRow(c, level + 1)));
      }
    }
    return rows;
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Preencha nome, e-mail e senha.");
    try {
      await api.post("/reseller/sub", { ...form, initial_credits: Number(form.initial_credits) });
      toast.success("Revendedor criado!");
      setOpen(false);
      setForm({ name: "", email: "", password: "", as_reseller: true, initial_credits: 0 });
      setChildrenMap({}); setExpanded({});
      loadRoots();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="reseller-network">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Minha rede</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-1" data-testid="create-sub-btn"><Plus className="h-4 w-4" /> Criar revendedor</Button></DialogTrigger>
          <DialogContent data-testid="create-sub-dialog">
            <DialogHeader><DialogTitle>Novo revendedor / usuário</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sub-name-input" /></div>
              <div className="space-y-2"><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sub-email-input" /></div>
              <div className="space-y-2"><Label>Senha</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sub-password-input" /></div>
              <div className="space-y-2"><Label>Créditos iniciais (transferidos do seu saldo)</Label><Input type="number" value={form.initial_credits} onChange={(e) => setForm({ ...form, initial_credits: e.target.value })} data-testid="sub-credits-input" /></div>
              <label className="flex items-center gap-2"><Switch checked={form.as_reseller} onCheckedChange={(v) => setForm({ ...form, as_reseller: v })} data-testid="sub-reseller-switch" /> Criar como revendedor</label>
              <Button className="w-full gap-1" onClick={create} data-testid="sub-create-submit"><Send className="h-4 w-4" /> Criar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="referral-card">
        <p className="text-sm font-medium">🔗 Seu link de indicação</p>
        <p className="mb-2 text-xs text-muted-foreground">Quem se cadastrar por este link entra na sua rede e pode comprar créditos de você.</p>
        <div className="flex items-center gap-2">
          <Input readOnly value={refLink} className="font-mono text-xs" data-testid="referral-link" />
          <Button variant="outline" size="icon" onClick={copyRef} data-testid="referral-copy-btn">{refCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}</Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">Seu saldo: <span className="font-medium text-foreground">{user?.credits} créditos</span>.</p>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar na rede por nome ou e-mail..." className="pl-9" data-testid="network-search-input" />
        </div>
      </div>

      {sr ? (
        <div className="space-y-2" data-testid="network-search-results">
          {sr.items.length === 0 && <p className="rounded-lg border border-dashed border-border py-12 text-center text-muted-foreground">Nenhum resultado para "{q}".</p>}
          {sr.items.map((node) => (
            <div key={node.id} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2" data-testid={`network-result-${node.id}`}>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                {node.role === "reseller" ? <Users className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{node.name}</p>
                <p className="truncate text-xs text-muted-foreground">{node.email}</p>
              </div>
              <Badge variant="secondary" className="capitalize text-[10px]">{node.role}</Badge>
              <span className="text-xs text-muted-foreground">{node.credits} cr.</span>
              {node.children_count > 0 && <span className="hidden text-[10px] text-muted-foreground sm:inline">{node.children_count} sub</span>}
            </div>
          ))}
          <Pagination page={sr.page} limit={sr.limit} total={sr.total} onPage={searchPage} label="pessoas" testid="network-pager" />
        </div>
      ) : (
        <div className="space-y-2">
          {roots.length === 0 && <p className="rounded-lg border border-dashed border-border py-12 text-center text-muted-foreground">Você ainda não possui revendedores. Crie o primeiro!</p>}
          {roots.flatMap((r) => renderRow(r, 0))}
        </div>
      )}
    </div>
  );
}
